export const handleApiError = (error) => {
  if (error.response) {
    // * The request was made and the server responded with a status code
    // * that fails out of the range of 2xx
    return {
      data: error.response.data,
      status: error.response.status,
      headers: error.response.headers,
    };
  }
  if (error.message) {
    return {
      data: error.message,
      status: 408,
    };
  }
  if (error.request) {
    // * The request was made but no response was received
    // * `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // * http.ClientRequest in node.js
    return error.request;
  }
  // * Something happened in setting up the request that triggered an Error
  return error;
};

export const sortAlphabetical = (list = [], sortBy) =>
  //* Alphabetical sorting
  {
    return list.sort((lastOne, nextOne) => {
      const aLast = lastOne[sortBy]?.charAt(0);
      const bLast = nextOne[sortBy]?.charAt(0);
      return aLast > bLast ? 1 : -1;
    });
  };

export const sortCourseAlphabetical = (list = [], sortBy) =>
  //* Alphabetical course sorting - doesn't use first char only as that caused incorrect sorting
  {
    return list.sort((lastOne, nextOne) => {
      const aLast = lastOne[sortBy];
      const bLast = nextOne[sortBy];
      return aLast > bLast ? 1 : -1;
    });
  };

export const removeDuplicates = (target) => {
  return [...new Set(target)];
};

export const getFlagEmoji = (countryCode) => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => {
      return 127397 + char.charCodeAt();
    });
  return String.fromCodePoint(...codePoints);
};

export const deleteDataCache = async (axiosCache, deleteCache = false, cacheID = '') => {
  const cache = await axiosCache?.storage?.get(cacheID);
  if (deleteCache && cache?.state === 'cached') {
    await axiosCache.storage.remove(cacheID);
  }
};

export const authTokenCheck = async (store, router) => {
  if (localStorage?.token !== undefined) {
    store.dispatch('authentication/checkToken').catch((err) => {
      console.error('err:', err);
      if (err.status >= 400 || !!err) {
        const alerted = localStorage.getItem('logoutAlert') || '';
        if (alerted !== 'yes') {
          const errorMessage = `User token invalid. You are being logged out. Please log back in.`;
          alert(`${errorMessage}`);
          localStorage.setItem('logoutAlert', 'yes');
        }

        store.dispatch('authentication/logout');
        store.dispatch('users/resetState');
        store.dispatch('licenses/resetLicenses');

        //* remove users preferences from local storage
        localStorage.removeItem('userPreferences');

        router.push('/login').catch((e) => {
          console.error('router error:', e);
        });
      }
    });
  }
};

export const isFalsey = (value) => {
  return !value;
};

//* cm to feet = divide by 30.48
//* cm to yards = divide by 91.44
//* cm to meters = divide by 100
//* cm/sec to fps = divide by 30.48
//* cm/sec to mph = divide by 44.704
//* feet to meters = divide by 3.281
//* feet to yards = divide by 3
//* yards to meters = divide by 1.094
export const unitConversion = (unitType, val, type, convert = false) => {
  // console.log(unitType);
  // console.log('val:', val);
  // console.log('type:', type);

  //* Conversion units
  const feetToYardsUnit = 3;
  const fpsUnit = 0.681818;
  const fpsMetricUnit = 1.09728;
  const feetToMetersUnit = 3.281;
  const cmToFeetUnit = 30.48;

  if (!val && val !== 0 && val !== 'undefined') return;

  if (convert) {
    // @ Update: V3 uses cm and V2 uses feet - need to convert
    // @ Update: V2 data being converted to cm uses feet - need to convert
    //* convert cm to feet
    //* convert cm/sec to fps

    //* feet to cm = * 30.48
    // 225.70242ft * 30.48 = 6879.4097616 cm

    const convertedVal = val / cmToFeetUnit;
    // 4.20406822 * 0.681818

    //* cm standard value converted to feet
    switch (unitType) {
      case 'Imperial':
      case 'imperial':
        return type === 'fps'
          ? Math.abs(roundNum(convertedVal * fpsUnit))
          : type === 'feet'
          ? Number(convertedVal)?.toFixed(1)
          : roundNum(convertedVal / feetToYardsUnit);
      case 'Metric':
      case 'metric':
        return type === 'fps'
          ? Math.abs(roundNum(convertedVal * fpsMetricUnit))
          : roundNum(convertedVal / feetToMetersUnit);
      default:
        return type === 'fps'
          ? Math.abs(roundNum(convertedVal * fpsUnit))
          : type === 'feet'
          ? Number(convertedVal)?.toFixed(1)
          : roundNum(convertedVal / feetToYardsUnit);
    }
  } else {
    //* feet standard value
    switch (unitType) {
      case 'Imperial':
      case 'imperial':
        return type === 'fps'
          ? Math.abs(roundNum(val * fpsUnit))
          : type === 'feet'
          ? Number(val)?.toFixed(1)
          : roundNum(val / feetToYardsUnit);
      case 'Metric':
      case 'metric':
        return type === 'fps'
          ? Math.abs(roundNum(val * fpsMetricUnit))
          : roundNum(val / feetToMetersUnit);
      default:
        return type === 'fps'
          ? Math.abs(roundNum(val * fpsUnit))
          : type === 'feet'
          ? Number(val)?.toFixed(1)
          : roundNum(val / feetToYardsUnit);
    }
  }
};

const roundNum = (num) => {
  if (!num && num !== 0) return;
  return Number(num)?.toFixed(2);
};

export const metaSet = (to, from, next) => {
  //* setup to set meta tags dynamically on pages============================
  // This goes through the matched routes from last to first, finding the closest route with a title.
  // e.g., if we have `/some/deep/nested/route` and `/some`, `/deep`, and `/nested` have titles,
  // `/nested`'s will be chosen.
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => {
      return r.meta && r.meta.title;
    });

  //* Find the nearest route element with meta tags.
  const nearestWithMeta = to.matched
    .slice()
    .reverse()
    .find((r) => {
      return r.meta && r.meta.metaTags;
    });

  const previousNearestWithMeta = from.matched
    .slice()
    .reverse()
    .find((r) => {
      return r.meta && r.meta.metaTags;
    });

  //* If a route with a title was found, set the document (page) title to that value.
  if (nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else if (previousNearestWithMeta) {
    document.title = previousNearestWithMeta.meta.title;
  }

  //* Remove any stale meta tags from the document using the key attribute we set below.
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map((el) => {
    return el.parentNode.removeChild(el);
  });

  //* Skip rendering meta tags if there are none.
  if (!nearestWithMeta) return next();

  //* Turn the meta tag definitions into actual elements in the head.
  nearestWithMeta.meta.metaTags
    .map((tagDef) => {
      const tag = document.createElement('meta');

      Object.keys(tagDef).forEach((key) => {
        tag.setAttribute(key, tagDef[key]);
      });

      // We use this to track which meta tags we create so we don't interfere with other ones.
      tag.setAttribute('data-vue-router-controlled', '');

      return tag;
    })
    //* Add the meta tags to the document head.
    .forEach((tag) => {
      return document.head.appendChild(tag);
    });
};

export const isDev = () => {
  const isQASite =
    window.location.host === 'qa.e6golf.com' ||
    window.location.host === 'localhost:8080' ||
    window.location.host?.includes('808');
  return isQASite;
};

export const getFirstLetters = (str) => {
  const firstLetters = str
    .split(' ')
    .map((word, i) => {
      // return word.charAt(0);
      return i % 2 === 0 ? word?.charAt(0)?.toUpperCase() : word?.charAt(0)?.toLowerCase();
    })
    .join('');

  return firstLetters;
};

export const sleep = (ms) => {
  return new Promise((resolve) => {
    return setTimeout(resolve, ms);
  });
};

// export const uniqueArray = (arr) => {
//   console.log('arr:', arr);
//   const jsonObj = arr.map(JSON.stringify);
//   const uniqueSet = new Set(jsonObj);
//   const uniqueArr = Array.from(uniqueSet).map(JSON.parse);
//   console.log('uniqueArr:', uniqueArr);
//   return [...new Set(arr)];
// };

export const uniqueArray = (arr) => {
  const ids = arr.map(({ id }) => {
    return id;
  });
  const filtered = arr.filter(({ id }, index) => {
    return !ids.includes(id, index + 1);
  });
  return filtered;
};

export const uppercaseWords = (str) => {
  return str?.replace(/^(.)|\s+(.)/g, (c) => {
    return c?.toUpperCase();
  });
};

export const hexToRGBA = (color = '#ffffff', opacity = 1) => {
  // Convert each hex character pair into an integer
  const red = parseInt(color.substring(1, 3), 16);
  const green = parseInt(color.substring(3, 5), 16);
  const blue = parseInt(color.substring(5, 7), 16);

  // Concatenate these codes into proper RGBA format
  const rgba = ` rgba(${red}, ${green}, ${blue}, ${opacity})`;

  return rgba;
};

// @ Example
// created() {
//   this.debouncedFetch = debounce((newValue, oldValue) => {
//     // console.log(‘value changed: ’, newValue, oldValue);
//     this.localData.notes = newValue;
//     //* call API to get results
//     this.updateFormatting(this.localData, newValue, ‘notes’);
//   }, 1500);
// },

// ‘localData.notes’: {
//   handler(...args) {
//     // console.log(‘args:’, args);
//     this.debouncedFetch(...args);
//   },
//   deep: true,
//   immediate: false,
// },
// @ =======================
export function debounce(fn, wait) {
  let timer;
  return function (...args) {
    if (timer) {
      clearTimeout(timer); // clear any pre-existing timer
    }
    const context = this; // get the current context
    timer = setTimeout(() => {
      fn.apply(context, args); // call the function if time expires
    }, wait);
  };
}

export function throttle(fn, wait) {
  let throttled = false;
  return function (...args) {
    if (!throttled) {
      fn.apply(this, args);
      throttled = true;
      setTimeout(() => {
        throttled = false;
      }, wait);
    }
  };
}

export const hasApexPermissionCheck = (arr = []) => {
  let checkPerms = false;
  for (let i = 0; i < arr?.length; i++) {
    checkPerms = arr?.[i]?.permissions?.some((perm) => {
      return perm?.key === 'license_apex_demo' || perm?.key === 'apex_core';
    });
    if (checkPerms) {
      break;
    }
  }

  return checkPerms;
};

export const objHasField = (field = '', obj = {}) => {
  const hasVal = field in obj;
  return hasVal;
};

export const parseJwt = (token) => {
  if (!token) return;
  const base64Url = token?.split('.')[1];
  const base64 = base64Url?.replace(/-/g, '+')?.replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      ?.split('')
      ?.map((c) => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
      })
      ?.join('')
  );

  return JSON.parse(jsonPayload);
};

// Create a Set of membership IDs for O(1) lookup to use for checking membership status
export const isMemberOfClubhouse = (clubhouse = {}, clubMemberships = []) => {
  const membershipIds = new Set(
    clubMemberships.map((membership) => {
      return membership.uuid;
    })
  );
  return membershipIds.has(clubhouse.uuid);
};

// For a checking a single clubhouse
export const checkSingleClubhouse = (clubhouse, clubMemberships) => {
  return isMemberOfClubhouse(clubhouse, clubMemberships) ?? false;
};

// For checking against all clubhouses
export const getAllMemberships = (clubhouses = [], clubMemberships = []) => {
  return clubhouses.map((clubhouse) => {
    return {
      clubhouseName: clubhouse.name,
      isMember: isMemberOfClubhouse(clubhouse, clubMemberships) ?? false,
    };
  });
};

export const formatGameModeKey = (mode = '') => {
  const modeFormat = mode.toLowerCase();

  switch (modeFormat) {
    case 'closest to pin':
    case 'closest to the pin':
    case 'closesttopin':
      return 'closest_to_pin';
    case 'stroke play':
    case 'strokeplay':
      return 'stroke_play';
    case 'best ball':
    case 'bestball':
      return 'best_ball';
    case 'longest drive':
    case 'longestdrive':
      return 'longest_drive';
    case 'match play':
    case 'matchplay':
      return 'match_play';
    default:
      return modeFormat;
  }
};
